import React, { useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import Modal from "../../shared/ScopedModal";
import useUpscaleOperation from "../../../hooks/useUpscaleOperation";
import UpscaleInspector from "../../shared/UpscaleInspector";
import { trackEvent } from "../utils/cropRatioValidation";
import { upscaleTestGroupProps } from "./upscaleTracking";

export default function CustomerUpscale({
  upscale_available,
  frame_upscale_path,
  media_type,
  image,
  ai_icon_path,
  ai_icon_loading_path,
  open: controlledOpen,
  onOpenChange,
  source,
  showTrigger = true,
}) {
  const controlled = controlledOpen !== undefined;
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlled ? controlledOpen : localOpen;
  const setOpen = (value) =>
    controlled ? onOpenChange?.(value) : setLocalOpen(value);
  const [showComparison, setShowComparison] = useState(false);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setShowComparison(false);
      trackEvent("Upscale Modal: Opened", {
        image_id: image.id,
        media_type,
        source: source || "inline_link",
        ...upscaleTestGroupProps(),
      });
    }
    wasOpen.current = open;
  }, [open]);
  return (
    <span>
      {showTrigger &&
        !image.ai_upscaled &&
        upscale_available === "available" && (
          <span>
            try our{" "}
            <button
              onClick={() => setOpen(true)}
              className="inline cursor-pointer underline decoration-gray-900/45"
            >
              AI Upscaler
            </button>{" "}
            or{" "}
          </span>
        )}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        classNames={{
          overlay: "custom-overlay",
          modal: `custom-modal upscale-modal relative h-[100dvh] sm:h-[calc(100dvh_-_2.4rem)] overflow-y-auto${
            showComparison ? " w-full sm:w-11/12 max-w-6xl" : ""
          }`,
        }}
      >
        {open && (
          <CustomerReview
            endpoint={frame_upscale_path}
            image={image}
            mediaType={media_type}
            iconPath={ai_icon_path}
            loadingIconPath={ai_icon_loading_path}
            onComparisonChange={setShowComparison}
            onClose={() => setOpen(false)}
          />
        )}
      </Modal>
    </span>
  );
}

const BUBBLES = Array.from({ length: 40 }, () => ({
  "--size": `${2 + Math.random() * 4}rem`,
  "--distance": `${11 + Math.random() * 4}rem`,
  "--position": `${-5 + Math.random() * 110}%`,
  "--time": `${2 + Math.random() * 2}s`,
  "--delay": `${-2 - Math.random() * 2}s`,
}));

const BUBBLE_CSS = `
.upscale-bubbles { position: absolute; left: -2rem; right: -2rem; bottom: -2rem; height: 6rem; background: rgb(243 242 240); filter: url(#upscale-blob); }
.upscale-bubble { position: absolute; left: var(--position); background: rgb(243 242 240); border-radius: 100%; transform: translate(-50%, 100%);
  animation: upscale-bubble-size var(--time) ease-in infinite var(--delay), upscale-bubble-move var(--time) ease-in infinite var(--delay); }
@keyframes upscale-bubble-size { 0%, 75% { width: var(--size); height: var(--size); } 100% { width: 0; height: 0; } }
@keyframes upscale-bubble-move { 0% { bottom: -4rem; } 100% { bottom: var(--distance); } }
@media (prefers-reduced-motion: reduce) { .upscale-bubbles { display: none; } }
`;

const ProcessingBubbles = () => (
  <div
    aria-hidden="true"
    className="absolute inset-x-0 bottom-0 h-80 overflow-hidden pointer-events-none"
  >
    <style>{BUBBLE_CSS}</style>
    <div className="upscale-bubbles">
      {BUBBLES.map((style, i) => (
        <div key={i} className="upscale-bubble" style={style} />
      ))}
    </div>
    <div className="absolute inset-x-0 bottom-0 h-12 bg-[#F3F2F0]" />
    <svg className="absolute w-0 h-0">
      <filter id="upscale-blob">
        <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
        <feColorMatrix
          in="blur"
          mode="matrix"
          values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -9"
        />
      </filter>
    </svg>
  </div>
);

function CustomerReview({
  endpoint,
  image,
  mediaType,
  iconPath,
  loadingIconPath,
  onComparisonChange,
  onClose,
}) {
  const identity = useMemo(
    () => ({ image: { token: image.upscale_token }, media_type: mediaType }),
    [image.upscale_token, mediaType],
  );
  const [detailShown, setDetailShown] = useState(false);
  const details = (operation) => ({
    image_id: image.id,
    ...upscaleTestGroupProps(),
    model: operation?.model,
    media_type: mediaType,
    before_width: operation?.before_dimensions?.width,
    before_height: operation?.before_dimensions?.height,
    after_width: operation?.after_dimensions?.width,
    after_height: operation?.after_dimensions?.height,
    detail_panels_shown: detailShown,
  });
  const flow = useUpscaleOperation({
    endpoint,
    identity,
    onComplete: (data) => {
      trackEvent("Upscale Modal: Accepted", details(data));
      if (data.redirect_to) window.location.href = data.redirect_to;
    },
  });
  const [ready, setReady] = useState(false);
  const op = flow.operation;
  useEffect(() => {
    if (op?.redirect_to) window.location.href = op.redirect_to;
  }, [op?.redirect_to]);
  useEffect(() => {
    onComparisonChange(Boolean(op?.after_url));
  }, [op?.after_url, onComparisonChange]);
  const waiting = flow.busy || op?.processing || op?.applying;
  const error = flow.error || op?.error;
  const trackedError = useRef(null);
  useEffect(() => {
    if (error && error !== trackedError.current) {
      trackEvent(
        op?.decision === "apply_failed"
          ? "Upscale Modal: Save Error"
          : "Upscale Modal: Error",
        { ...details(op), error },
      );
    }
    trackedError.current = error || null;
  }, [error]);
  const start = async (retry) => {
    const data = await flow.start(retry);
    trackEvent("Upscale Modal: Processed", {
      image_id: image.id,
      ...upscaleTestGroupProps(),
      retry,
      media_type: mediaType,
      model: data?.model,
      source_width: image.width,
      source_height: image.height,
    });
  };
  const close = async () => {
    trackEvent("Upscale Modal: Cancelled", {
      ...details(op),
      stage: op?.after_url ? "review" : "processing",
    });
    if (await flow.cancel()) onClose();
  };
  const buttonSize = "px-5 py-3 text-base";
  const buttonState =
    "disabled:bg-stone-400 disabled:border-stone-400 disabled:text-white disabled:cursor-not-allowed";
  return (
    <div
      className={`modal-scroll flex flex-col h-full${op?.after_url ? " min-h-[560px]" : ""}`}
    >
      {error && (
        <div role="alert" className="alert alert-danger mb-4">
          <i className="fa-solid fa-triangle-exclamation mr-2" />
          {error}
        </div>
      )}
      {!op?.after_url ? (
        <div className="text-center max-w-2xl mx-auto my-auto">
          <img
            src={waiting ? loadingIconPath : iconPath}
            alt=""
            className="w-24 mx-auto"
          />
          <h2 className="mb-3">Increase Your Photo's Resolution</h2>
          <p className="text-lg">
            AI Upscale can increase the resolution of your photo for better
            print quality and more size options.
          </p>
          {(!op || op.processing || op.can_retry) && (
            <button
              onClick={() => start(Boolean(op?.can_retry))}
              disabled={waiting}
              className="btn btn-primary btn-lg mt-5 disabled:bg-stone-400 disabled:border-stone-400 disabled:text-white disabled:cursor-not-allowed"
            >
              {waiting ? (
                <span role="status">Processing...</span>
              ) : op?.can_retry ? (
                "Retry processing"
              ) : (
                "Try it"
              )}
              {waiting && (
                <i className="fa-solid fa-spinner-third fa-spin ml-2" />
              )}
            </button>
          )}
          {waiting && (
            <p className="text-sm text-stone-600 mt-3">
              This can take 10–15 seconds...
            </p>
          )}
          {waiting && <ProcessingBubbles />}
          {op && !waiting && (
            <button
              onClick={close}
              className="btn btn-primary btn-pale mt-5 ml-3"
            >
              No thanks
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Right padding keeps the actions clear of the modal's close icon. */}
          <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 mb-3 sm:pr-12">
            <div>
              <h2 className="mb-1 text-2xl tracking-tight">
                Verify Upscaled Photo
              </h2>
              <p className="text-sm">
                {isMobile ? "Tap on" : "Hover over"} important details in the
                image to compare.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-between sm:justify-start gap-x-4 gap-y-2 mt-[15px] w-full sm:w-auto">
              {op?.can_retry && (
                <button
                  onClick={() => start(true)}
                  disabled={waiting}
                  className={`btn btn-primary ${buttonSize}`}
                >
                  Retry processing
                </button>
              )}
              <button
                onClick={close}
                disabled={flow.busy || op?.applying}
                className="text-sm underline underline-offset-2 decoration-stone-400 text-stone-700 hover:text-stone-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                No thanks
              </button>
              {(op?.can_accept || op?.applying) && (
                <button
                  onClick={flow.accept}
                  disabled={waiting || !ready}
                  className={`btn btn-primary ${buttonSize} ${buttonState}`}
                >
                  {waiting ? (
                    <span role="status">Saving...</span>
                  ) : op.decision === "apply_failed" ? (
                    "Retry saving"
                  ) : (
                    "Approve Image"
                  )}
                  <i
                    aria-hidden="true"
                    className={
                      waiting
                        ? "fa-solid fa-spinner-third fa-spin ml-2"
                        : "fa-solid fa-check ml-2"
                    }
                  />
                </button>
              )}
            </div>
          </div>
          <UpscaleInspector
            operation={op}
            onReady={setReady}
            onDetailChange={setDetailShown}
            showHint={false}
            showMegapixels
          />
        </>
      )}
    </div>
  );
}
